/**
 * CrudService
 * Concrete implementation of standard CRUD operations
 * Extends BaseService with generic entity type support
 */
import { BaseService } from './BaseService';
/**
 * Standard CRUD service with dual method pattern
 * - Public methods return unwrapped data
 * - Raw methods return full ResponseData<T>
 *
 * @template TEntity - The entity type (e.g., Task)
 * @template TCreateDto - The create DTO type (defaults to Partial<TEntity>)
 * @template TUpdateDto - The update DTO type (defaults to Partial<TEntity>)
 */
export class CrudService extends BaseService {
    constructor(http, config) {
        super(http, config);
    }
    // ==================== GET ALL ====================
    /**
     * Get all entities
     * @returns Array of entities
     */
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    /**
     * Get all entities with full response
     * @returns ResponseData with array of entities
     */
    getAllRaw() {
        const url = this.buildUrl();
        return this.http.get(url);
    }
    // ==================== GET BY ID ====================
    /**
     * Get entity by ID
     * @param id - Entity ID
     * @returns The entity
     * @throws Error if entity not found
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Entity with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get entity by ID with full response
     * @param id - Entity ID
     * @returns ResponseData with entity
     */
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    // ==================== CREATE ====================
    /**
     * Create a new entity
     * @param data - Entity data
     * @returns The created entity
     * @throws Error if creation failed
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create entity');
        }
        return response.data;
    }
    /**
     * Create a new entity with full response
     * @param data - Entity data
     * @returns ResponseData with created entity
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing entity
     * @param id - Entity ID
     * @param data - Updated entity data
     * @returns The updated entity
     * @throws Error if update failed
     */
    async update(id, data) {
        const response = await this.updateRaw(id, data);
        if (!response.data) {
            throw new Error(`Failed to update entity with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Update an existing entity with full response
     * @param id - Entity ID
     * @param data - Updated entity data
     * @returns ResponseData with updated entity
     */
    updateRaw(id, data) {
        const url = this.buildUrl(String(id));
        return this.http.put(url, data);
    }
    // ==================== DELETE ====================
    /**
     * Delete an entity
     * @param id - Entity ID
     * @returns True if deleted successfully
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Delete an entity with full response
     * @param id - Entity ID
     * @returns ResponseData (success/fail)
     */
    deleteRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.delete(url);
    }
    // ==================== PATCH ====================
    /**
     * Partially update an entity
     * @param id - Entity ID
     * @param data - Partial entity data
     * @returns The updated entity
     * @throws Error if update failed
     */
    async patch(id, data) {
        const response = await this.patchRaw(id, data);
        if (!response.data) {
            throw new Error(`Failed to patch entity with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Partially update an entity with full response
     * @param id - Entity ID
     * @param data - Partial entity data
     * @returns ResponseData with updated entity
     */
    patchRaw(id, data) {
        const url = this.buildUrl(String(id));
        return this.http.patch(url, data);
    }
}
//# sourceMappingURL=CrudService.js.map